import React from "react";
import { Box, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import "../../OurService/styles/RiskBasedOnLocation.css";

const SUPPORT_EMAIL = "info@myredfolder.com";

const ACCENT = "#b71c1c";
const MUTED = "#6b625b";
const RULE = "#e6dfd2";

const SITE = "MYREDFOLDER® / www.myredfolder.com";

const PILLS = [
  "Information we collect",
  "How we use it",
  "Security measures",
  "Your rights",
  "Policy updates",
];

// `text` may be a string or an array of paragraphs.
const SECTIONS = [
  {
    title: "Introduction",
    items: [
      {
        text: `Welcome to MYREDFOLDER® website ("we," "us," or "our"). At "MYREDFOLDER®," we take your privacy and security very seriously. This Privacy Policy outlines how we collect, use, and protect your personal information, digital assets, financial information, and other critical data when you use our website or services.`,
      },
      {
        text: `By accessing or using the MYREDFOLDER®, you acknowledge that you have read, understood, and agreed to the terms of this Privacy Policy. If you do not agree with our practices, please do not use the MYREDFOLDER®.`,
      },
    ],
  },
  {
    title: "Information We Collect",
    grid: true,
    items: [
      {
        title: "Personal Information",
        text: [
          "When you sign up for MYREDFOLDER®, we collect personal information, such as your name, email address, contact details, and other necessary identifiers.",
          "To enhance security, we may use multi-factor authentication methods to verify your identity during login and access to sensitive information.",
        ],
      },
      {
        title: "Digital Assets",
        text: [
          "The MYREDFOLDER® provides a secure platform to store and manage your digital assets, which may include usernames and passwords for cryptocurrencies, digital tokens, or other virtual assets.",
          "We use blockchain technology security to ensure the immutability and integrity of your digital assets.",
        ],
      },
      {
        title: "Financial Information",
        text: [
          "To facilitate secure transactions and payments, we may collect your financial information, such as bank account details, credit/debit card information, or cryptocurrency wallet addresses.",
          "All financial information is encrypted using the latest SSL security measures and stored in a secure, redundant server environment.",
        ],
      },
      {
        title: "Usage Information",
        text: [
          "We may collect information about your interactions with MYREDFOLDER® and/or the www.myredfolder.com website, such as your browsing activities, login times, and IP addresses.",
          "This information is used for security monitoring, system analysis, and to improve the overall user experience.",
        ],
      },
    ],
  },
  {
    title: "How We Use Your Information",
    grid: true,
    items: [
      {
        title: "Providing Services",
        text: [
          `We use your personal information and digital assets to provide secure access and manage your account on ${SITE} website.`,
          "Your financial information is used to process transactions and facilitate secure payments such as product purchases, subscription renewals, and other authorized transactions.",
        ],
      },
      {
        title: "Enhancing Security",
        text: [
          "Your personal information and digital assets are protected through our advanced security measures, including blockchain technology and quantum layer security.",
          "We continuously monitor our systems for potential vulnerabilities and promptly respond to any detected threats or breaches.",
        ],
      },
      {
        title: "Improving User Experience",
        text: "We may analyze usage data to understand user preferences, enhance website performance, and optimize user interface and experience.",
      },
      {
        title: "Legal Compliance",
        text: "We may use and disclose your information as required by applicable laws, regulations, or court orders.",
      },
    ],
  },
  {
    title: "Security Measures",
    grid: true,
    items: [
      {
        title: "Blockchain Technology Security",
        text: `${SITE} website utilizes blockchain technology to provide enhanced security and transparency for your digital assets.`,
      },
      {
        title: "Redundant Servers",
        text: "We maintain and/or lease redundant servers and data backups to ensure uninterrupted service and data integrity.",
      },
      {
        title: "SSL Security Measures",
        text: `All data transmitted between your device and the ${SITE} website is encrypted using SSL protocols to prevent unauthorized access or interception.`,
      },
      {
        title: "Quantum Layer Security",
        text: "The MYREDFOLDER® and/or www.myredfolder.com website employs quantum-resistant encryption methods to protect against potential quantum-based attacks.",
      },
    ],
  },
  {
    title: "Unforeseen Occurrences",
    items: [
      {
        title: "System Breach",
        text: [
          "In the unlikely event of a system breach, we have incident response plans in place to promptly identify and mitigate any potential impact.",
          "We will notify you and appropriate authorities as required by applicable laws and regulations.",
        ],
      },
    ],
  },
  {
    title: "Third-Party Services",
    items: [
      {
        text: "The MYREDFOLDER® may integrate with third-party services or websites, which have their own privacy policies. We are not responsible for their practices; please review their privacy policies separately.",
      },
    ],
  },
  {
    title: "Accessing and Modifying Your Information",
    items: [
      {
        text: [
          `You have the right to access, update, or delete your personal information stored on the ${SITE} website.`,
          "If you have any concerns or inquiries regarding your information, please contact our customer support.",
        ],
      },
    ],
  },
  {
    title: "Changes to this Privacy Policy",
    items: [
      {
        text: [
          "The MYREDFOLDER® management team may update this Privacy Policy from time to time to reflect changes in our practices or applicable laws.",
          "We will notify you of any material changes through the website or email. Please review the Privacy Policy periodically for updates.",
        ],
      },
    ],
  },
];

const pad = (n) => String(n).padStart(2, "0");

function Row({ index, section }) {
  return (
    <Box
      component="section"
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "260px 1fr" },
        columnGap: 6,
        rowGap: 2,
        py: { xs: 3, md: 5 },
        borderTop: `1px solid ${RULE}`,
      }}
    >
      <Box sx={{ position: { md: "sticky" }, top: { md: 90 }, alignSelf: "start" }}>
        <Typography
          sx={{
            color: ACCENT,
            fontWeight: 700,
            fontSize: { xs: 28, md: 44 },
            lineHeight: 1,
            letterSpacing: "-0.02em",
            mb: 1,
          }}
        >
          {pad(index + 1)}
        </Typography>
        <Typography component="h2" className="primary-font-bold11">
          {section.title}
        </Typography>
      </Box>

      <Box
        sx={
          section.grid
            ? {
                display: "grid",
                gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
                gap: { xs: 2.5, lg: 4 },
              }
            : { display: "flex", flexDirection: "column", gap: 2.5 }
        }
      >
        {section.items.map((item, i) => (
          <Box key={item.title || (Array.isArray(item.text) ? item.text[0] : item.text)}>
            {item.title && (
              <Typography
                component="h3"
                className="primary-font-bold15"
                sx={{ mb: 0.5 }}
              >
                <Box component="span" sx={{ color: MUTED, mr: 1, fontWeight: 500 }}>
                  {index + 1}.{i + 1}
                </Box>
                {item.title}
              </Typography>
            )}
            {[].concat(item.text).map((para) => (
              <Typography
                key={para}
                className="primary-font-normal-2"
                sx={{ lineHeight: 1.75, maxWidth: 1024, "& + &": { mt: 1 } }}
              >
                {para}
              </Typography>
            ))}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export default function PrivacyPolicy() {
  const navigate = useNavigate();

  return (
    <Box>
      {/* Hero */}
      <section
        className="mrf-banner mrf-banner--plain"
        style={{ height: "auto", padding: "28px 0 40px" }}
      >
        <div className="mrf-banner__inner">
          <Box
            onClick={() => navigate(-1)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && navigate(-1)}
            sx={{
              display: "none",
              alignItems: "center",
              alignSelf: "flex-start",
              gap: 0.5,
              cursor: "pointer",
              color: "#fff",
              opacity: 0.85,
              "&:hover": { opacity: 1 },
              mb: 2.5,
            }}
          >
            <ArrowBackIcon fontSize="small" />
            <Typography className="primary-font-medium-2" sx={{ color: "inherit" }}>
              Go Back
            </Typography>
          </Box>

          <p className="mrf-banner__eyebrow">Privacy Policy</p>
          <h1 className="mrf-banner__title" style={{ marginBottom: 24 }}>
            How we collect, use and protect your information
          </h1>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {PILLS.map((p) => (
              <Box
                key={p}
                sx={{
                  px: 1.5,
                  py: 0.5,
                  borderRadius: "999px",
                  color: "#fff",
                  backgroundColor: "rgba(255,255,255,0.1)",
                  border: "1px solid rgba(255,255,255,0.25)",
                  fontSize: 14,
                }}
              >
                {p}
              </Box>
            ))}
          </Box>
        </div>
      </section>

      {/* Sections */}
      <div className="ourservices-header-container">
        <Box sx={{ pb: { xs: 3, md: 5 } }}>
          {SECTIONS.map((section, i) => (
            <Row key={section.title} index={i} section={section} />
          ))}
        </Box>
      </div>

      {/* Contact band */}
      <section
        className="mrf-banner mrf-banner--plain"
        style={{ height: "auto", padding: "48px 0" }}
      >
        <div className="mrf-banner__inner">
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1.2fr 1fr" },
              gap: { xs: 3, md: 6 },
              alignItems: "center",
            }}
          >
            <Box>
              <p className="mrf-banner__eyebrow">Contact Us</p>
              <Typography
                component="h2"
                sx={{
                  fontFamily: '"Bebas Neue", "Poppins", sans-serif',
                  fontWeight: 700,
                  fontSize: "clamp(28px, 3.5vw, 44px)",
                  lineHeight: 1.05,
                  color: "#fff",
                  mb: 1.5,
                }}
              >
                Questions about your privacy?
              </Typography>
              <Typography
                className="primary-font-normal-2"
                sx={{ color: "rgba(255,255,255,0.8)", lineHeight: 1.7, maxWidth: 560 }}
              >
                If you have any questions, concerns, or feedback about this
                Privacy Policy or the {SITE} website, please contact our
                support team.
              </Typography>
            </Box>

            <Box
              component="a"
              href={`mailto:${SUPPORT_EMAIL}`}
              sx={{
                display: "block",
                textDecoration: "none",
                p: { xs: 2, md: 3 },
                borderRadius: "12px",
                backgroundColor: "rgba(255,255,255,0.08)",
                border: "1px solid rgba(255,255,255,0.18)",
                transition: "background-color 0.2s, border-color 0.2s",
                "&:hover, &:focus-visible": {
                  backgroundColor: "rgba(255,255,255,0.14)",
                  borderColor: "rgba(255,255,255,0.4)",
                },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: "50%",
                    backgroundColor: ACCENT,
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <MailOutlineIcon />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    className="primary-font-medium-2"
                    sx={{ color: "rgba(255,255,255,0.65)", fontSize: 13 }}
                  >
                    Email our support team
                  </Typography>
                  <Typography
                    className="primary-font-bold15"
                    sx={{ color: "#fff", wordBreak: "break-all" }}
                  >
                    {SUPPORT_EMAIL}
                  </Typography>
                </Box>
                <ArrowForwardIcon sx={{ color: "#fff", ml: "auto", flexShrink: 0 }} />
              </Box>
            </Box>
          </Box>

          <Typography
            className="primary-font-normal-2"
            sx={{
              color: "rgba(255,255,255,0.6)",
              lineHeight: 1.7,
              fontSize: 14,
              mt: { xs: 3, md: 5 },
              pt: 2.5,
              borderTop: "1px solid rgba(255,255,255,0.15)",
              maxWidth: 820,
            }}
          >
            Thank you for entrusting the MYREDFOLDER®, MW Concepts and
            Marketing, LLC, and the National Emergency Planning and Training
            Association, LLC with your personal and digital assets. We are
            committed to safeguarding your privacy and security.
          </Typography>
        </div>
      </section>
    </Box>
  );
}
